import { Modifier } from '../modifier';
import type { Damage } from '../damage.type';
import { addModifier, dealDamage, healthRemove } from '../player.type';
import { actionHistory } from '../../../store';
import type { Player } from '../player.type';
import type { Technique } from '../techniques.type';
import { DieFace } from '../face.type';
import { StatusEffect } from '../status-effects.type';
import { ModifierPriority, ModifierTags, type ModifierParams } from '../modifier.type';
import { StatusEffectRegistry } from '../status-effects';

export enum Stances {
  Retaliate = 'Retaliate', // on attacked, attack back
  Parry = 'Parry', // on attacked, negate damage and attack back
  Interrupt = 'Interrupt', // on enemy buff/debuff, negate defence and attack
  Deflect = 'Deflect', // on attacked, gain block
  Counterspell = 'Counterspell', // on enemy using mana, negate technique and gain infusion (mana)
  Prepared = 'Prepared', // on enemy using defence, attack before the defence resolves
  // Aggression = 'Aggression', // when attacking, gain 1 temp strength
  Blank = 'No Stance',
}

export const definitions: any = {
  [Stances.Retaliate]: {
    text: 'When attacked, deal 3 damage.',
  },
  [Stances.Parry]: {
    text: 'When attacked, negate damage and deal 2 damage.',
  },
  [Stances.Interrupt]: {
    text: 'When enemy uses a buff/debuff, prevent the technique and deal 2 damage.',
  },
  [Stances.Deflect]: {
    text: 'When attacked, gain 4 block.',
  },
  [Stances.Counterspell]: {
    text: 'When enemy uses mana, prevent the technique and gain 1 Infusion (Mana).',
  },
  [Stances.Prepared]: {
    text: 'When enemy uses a defence, deal 4 damage before the defence resolves.',
  },
};

export class Stance extends Modifier {
  static readonly baseTitle = 'Stance';
  static readonly baseText = 'Gain a stance. Can only have 1 stance active at a time.';
  static readonly baseIcon = 'stance';
  static readonly baseTags = [ModifierTags.Boon];
  static readonly basePriority = ModifierPriority.Early;

  // Instance properties that can change
  title = Stance.baseTitle;
  text = Stance.baseText;
  icon = Stance.baseIcon;
  tags: Array<ModifierTags> = [...Stance.baseTags];
  priority = Stance.basePriority;

  stance: Stances;

  constructor() {
    super();
    this.stance = Stances.Blank;
  }

  clone(newOwner: Player) {
    var clone = super.clone(newOwner);
    clone.stance = this.stance;

    return clone;
  }

  init(params: ModifierParams) {
    if (actionHistory) {
      actionHistory.push(`${this.owner.title} gained stance ${this.stance}.`);
    }
    this.update(0, params);
    this.title = `${Stance.baseTitle} (${this.stance})`;
  }

  update(stacks: number, params: ModifierParams) {
    if (!params.stance) {
      throw new Error('Stance modifier requires stance parameter.');
    }

    if (params.stance !== this.stance) {
      actionHistory.push(
        `${this.owner.title} changed stance from ${this.stance} to ${params.stance}.`,
      );
      this.stance = params.stance;
      this.title = `${Stance.baseTitle} (${this.stance})`;
      this.bonusIcon = this.stance;
    }

    this.stacks = 1;
  }

  canInterrupt(technique: Technique, source: Player, target: Player): { able: boolean } {
    switch (this.stance) {
      case Stances.Retaliate:
        if (technique.tags.includes('damage')) {
          dealDamage({ value: 3, target: source, source: target, dice: [] });
          this.removeStacks(1);
          return { able: true };
        }
        break;

      case Stances.Parry:
        if (technique.tags.includes('damage')) {
          dealDamage({ value: 2, target: source, source: target, dice: [] });
          this.removeStacks(1);
          return { able: false };
        }
        break;

      case Stances.Interrupt:
        if (technique.tags.includes('debuff') || technique.tags.includes('buff')) {
          dealDamage({ value: 2, target: source, source: target, dice: [] });
          this.removeStacks(1);
          return { able: false };
        }
        break;

      case Stances.Deflect:
        if (technique.tags.includes('damage')) {
          addModifier(target, StatusEffect.Block, 4);
          this.removeStacks(1);
          return { able: true };
        }
        break;

      case Stances.Counterspell:
        if (technique.ingredients.includes(DieFace.Mana)) {
          addModifier(target, StatusEffect.Infusion, 1, { infusion: DieFace.Mana });
          this.removeStacks(1);
          return { able: false };
        }
        break;

      case Stances.Prepared:
        if (technique.tags.includes('defence')) {
          dealDamage({ value: 4, target: source, source: target, dice: [] });
          this.removeStacks(1);
          return { able: true };
        }
        break;
    }

    return {
      able: true,
    };
  }

  getHTML(): string {
    let definition: any = definitions[this.stance];
    return `
      <h3> ${this.stance} </h3>
      <p> ${definition.text} </p>
    `;
  }
}

// Register the status effect
StatusEffectRegistry.register(StatusEffect.Stance, Stance);
